import { Stack, Typography } from '@mui/material';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import { ListItemToggle } from '@components/ListItem';
import RoundedButton from '@components/RoundedButton';
import UserManagementTable from '@components/Tables/OrgUserManagementTable';

import { ADD_ORG_USER } from '@constants/routes';

import { useGetAccountUsers } from '@hooks/api/account';
import {
  useAccountSettings,
  useUpdateAccountUserAddedEmailNotificationSetting,
  useUpdateAccountSettings,
} from '@hooks/api/account-settings';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useNavigate } from '@hooks/useNavigate';

const messages = defineMessages({
  title: {
    id: 'organization.setting.users.title',
    defaultMessage: 'User Management',
  },
  button: {
    id: 'organization.setting.users.addUser',
    defaultMessage: 'Add User',
  },
  patientMfaTitle: {
    id: 'organization.setting.mfa.patient.title',
    defaultMessage: 'Patient MFA',
  },
  patientMfaSubtitle: {
    id: 'organization.setting.mfa.patient.subtitle',
    defaultMessage: 'Require patients to set up MFA when logging in.',
  },
  practitionerMfaTitle: {
    id: 'organization.setting.mfa.practitioner.title',
    defaultMessage: 'Practitioner User MFA',
  },
  practitionerMfaSubtitle: {
    id: 'organization.setting.mfa.practitioner.subtitle',
    defaultMessage:
      'Require practitioners and org admins to set up MFA when logging in.',
  },
  userAddedEmailNotificationsTitle: {
    id: 'organization.setting.userAddedEmailNotifications.title',
    defaultMessage: 'New user notification emails',
  },
  userAddedEmailNotificationsSubtitle: {
    id: 'organization.setting.userAddedEmailNotifications.subtitle',
    defaultMessage:
      'Notify practitioners and org admins when new practitioners or org admins are added.',
  },
});

interface UserManagementProps {
  account_id: string;
  /** Is the viewing user a member of the org? */
  is_org: boolean;
}

function UserManagement({ account_id, is_org }: UserManagementProps) {
  const { formatMessage } = useIntl();
  const { navigateRoute } = useNavigate();
  const { flags } = useFeatureFlags();

  const { data, loading: usersLoading } = useGetAccountUsers(account_id);
  const { accountSettings, loading: accountSettingsLoading } =
    useAccountSettings(account_id);
  const [updateAccountSetting] = useUpdateAccountSettings();
  const [
    updateAccountUserAddedEmailNotificationSetting,
    updateAccountUserAddedEmailNotificationSettingLoading,
  ] = useUpdateAccountUserAddedEmailNotificationSetting();

  const loading = usersLoading || accountSettingsLoading;

  const serverPatientMfa =
    accountSettings?.getAccountSetting?.is_patient_mfa_required;
  const serverPractitionerMfa =
    accountSettings?.getAccountSetting?.is_practitioner_mfa_required;
  const serverUserAddedEmailNotifications =
    accountSettings?.getAccountSetting
      ?.account_user_added_email_notifications_enabled;

  const [localPatientMfa, setLocalPatientMfa] = React.useState<boolean>();
  const [localPractitionerMfa, setLocalPractitionerMfa] =
    React.useState<boolean>();
  const [
    localUserAddedEmailNotifications,
    setLocalUserAddedEmailNotifications,
  ] = React.useState<boolean>();

  // Sync local state when server data loads or updates
  React.useEffect(() => {
    if (serverPatientMfa !== undefined) setLocalPatientMfa(serverPatientMfa);
  }, [serverPatientMfa]);

  React.useEffect(() => {
    if (serverPractitionerMfa !== undefined)
      setLocalPractitionerMfa(serverPractitionerMfa);
  }, [serverPractitionerMfa]);

  React.useEffect(() => {
    if (serverUserAddedEmailNotifications !== undefined)
      setLocalUserAddedEmailNotifications(serverUserAddedEmailNotifications);
  }, [serverUserAddedEmailNotifications]);

  // Use local state (instant) with server fallback
  const isPatientMfaEnabled = localPatientMfa ?? serverPatientMfa;
  const isPractitionerMfaEnabled =
    localPractitionerMfa ?? serverPractitionerMfa;
  const areUserAddedEmailNotificationsEnabled =
    localUserAddedEmailNotifications ??
    serverUserAddedEmailNotifications ??
    true;

  const handlePatientMfaToggle = (enabled: boolean) => {
    setLocalPatientMfa(enabled);
    updateAccountSetting({
      account_id,
      is_patient_mfa_required: enabled,
    });
  };

  const handlePractitionerMfaToggle = (enabled: boolean) => {
    setLocalPractitionerMfa(enabled);
    updateAccountSetting({
      account_id,
      is_practitioner_mfa_required: enabled,
    });
  };

  const handleUserAddedEmailNotificationsToggle = (enabled: boolean) => {
    setLocalUserAddedEmailNotifications(enabled);
    updateAccountUserAddedEmailNotificationSetting({
      account_id,
      enabled,
    });
  };

  return (
    <ContentLoader lineCount={4} loading={loading}>
      <Stack direction="row" justifyContent={'space-between'}>
        <Typography>{formatMessage(messages.title)}</Typography>

        <RoundedButton
          autoSize
          iconName="plus"
          onClick={() =>
            navigateRoute(ADD_ORG_USER, is_org ? undefined : { account_id })
          }
        >
          {formatMessage(messages.button)}
        </RoundedButton>
      </Stack>
      <ListItemToggle
        disabled={!flags.isAccountUpdateAccountSettingEnabled}
        enabled={isPractitionerMfaEnabled}
        icon_name="user-shield"
        onToggle={handlePractitionerMfaToggle}
        primary_text={formatMessage(messages.practitionerMfaTitle)}
        secondary_text={formatMessage(messages.practitionerMfaSubtitle)}
      />
      <ListItemToggle
        disabled={
          updateAccountUserAddedEmailNotificationSettingLoading ||
          !flags.isAccountUpdateAccountUserAddedEmailNotificationSettingEnabled
        }
        enabled={areUserAddedEmailNotificationsEnabled}
        icon_color="text.primary"
        icon_name="bell"
        onToggle={handleUserAddedEmailNotificationsToggle}
        primary_text={formatMessage(messages.userAddedEmailNotificationsTitle)}
        secondary_text={formatMessage(
          messages.userAddedEmailNotificationsSubtitle
        )}
      />
      <ListItemToggle
        disabled={!flags.isAccountUpdateAccountSettingEnabled}
        enabled={isPatientMfaEnabled}
        icon_name="user-shield"
        onToggle={handlePatientMfaToggle}
        primary_text={formatMessage(messages.patientMfaTitle)}
        secondary_text={formatMessage(messages.patientMfaSubtitle)}
      />
      <UserManagementTable data={data} title={formatMessage(messages.title)} />
    </ContentLoader>
  );
}

export default withErrorBoundary(UserManagement, {
  FallbackComponent: ErrorBoundaryFallback,
});
